import { cloneElement, memo, useRef, useState, useMemo, useCallback } from 'react';
import { cn } from '@divlab/divanui';

import useMedias from '@Hooks/useMedias';
import useScrollPosition from '@Hooks/useScrollPosition';
import useViewport from '@Hooks/useViewport';
import styles from './ScrollAnimateContainer.module.css';

import type { FC, ReactElement } from 'react';

export interface StageProps {
  timing: number;
  component: ReactElement;
}

export interface StageComponentProps {
  stage: StageProps;
  progress: number;
  active: boolean;
}

export interface ScrollAnimateContainerProps {
  className?: string;
  stages: StageProps[];
}

export interface StageProgressData {
  progress: number;
  index: number;
}

const Stage = (props: StageComponentProps) => {
  const { stage, progress } = props;

  return (
    <div>
      {cloneElement(stage.component, {
        progress,
      })}
    </div>
  );
};

const ScrollAnimateContainer: FC<ScrollAnimateContainerProps> = (props) => {
  const { stages, ...restProps } = props;
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const { height: viewportHeight } = useViewport();
  const { isDesktop } = useMedias();

  const initialProgress = useMemo(() => {
    return stages.map((stage, index) => ({
      index,
      progress: 0,
    }));
  }, [stages]);

  const [stageProgress, setStageProgress] = useState<StageProgressData[]>(initialProgress);

  const handleScroll = useCallback(({ index, progress }) => {
    setStageProgress((prevState) => {
      return prevState.map((item, idx) => {
        if (idx !== index) {
          return item;
        }

        return { ...item, progress };
      });
    });
  }, []);

  useScrollPosition(() => {
    const placeholder = ref.current;
    if (!placeholder) return;

    Array.from(placeholder.children).forEach((child, index) => {
      const { height, bottom, top } = child.getBoundingClientRect();
      const startProgressPosition = isDesktop
        ? bottom - height / 2 - viewportHeight
        : top - viewportHeight / 2;
      const endProgressPosition = isDesktop
        ? startProgressPosition + viewportHeight - height / 4
        : startProgressPosition + viewportHeight / 2 - 150;

      if (startProgressPosition <= 0 && endProgressPosition >= 0) {
        setActive(index);
        const progress =
          (startProgressPosition / (endProgressPosition - startProgressPosition)) * -100;

        handleScroll({ index, progress });
      } else if (startProgressPosition > 0) {
        handleScroll({ index, progress: 0 });
      } else if (endProgressPosition < 0) {
        handleScroll({ index, progress: 100 });
      }
    });
  });

  return (
    <div {...restProps} className={cn(styles.wrapper)}>
      <div ref={ref} className={styles.container}>
        {stages.map((stage, index) => (
          <Stage
            key={index}
            stage={stage}
            progress={stageProgress[index].progress}
            active={active === index}
          />
        ))}
      </div>
    </div>
  );
};

export default memo(ScrollAnimateContainer);
